<template>
    <div class="tw:w-full tw:sm:w-auto">
        <div ref="dropdownRef" class="tw:relative tw:w-full tw:sm:w-auto" :class="containerClass">
            <Button
                type="button"
                :data-cy="dataCy"
                :disabled="disabled"
                :loading="loading"
                :custom-class="buttonClass"
                @click="toggle"
            >
                {{ label }}
            </Button>
        </div>
        <Portal to="dropdown-root">
            <div
                v-if="internalExpanded"
                ref="panelRef"
                :data-cy="`${dataCy}-panel`"
                :class="[
                    'tw:rounded-lg tw:shadow-xl tw:bg-slate-800 tw:border tw:border-slate-700 tw:overflow-y-auto',
                ]"
                :style="panelStyle"
            >
                <div v-for="(option, index) in options" :key="index">
                    <div
                        v-if="isDanger(option) && index > 0"
                        class="tw:border-t tw:border-slate-700 tw:my-1"
                    ></div>
                    <Button
                        v-if="option.type === 'action'"
                        type="button"
                        align="left"
                        :custom-class="'tw:w-full tw:bg-transparent tw:normal-case tw:font-normal tw:font-sans tw:text-sm tw:text-slate-200 tw:hover:text-white'"
                        @click="closeAndTrigger(option.action)"
                    >
                        <i
                            v-if="getIcon(option)"
                            :class="`fas fa-${getIcon(option)}`"
                            class="tw:w-4 tw:text-slate-400 tw:group-hover:text-white tw:transition-colors"
                            aria-hidden="true"
                        ></i>
                        {{ formatLabel(option.label) }}
                    </Button>

                    <a
                        v-else-if="option.type === 'url'"
                        :href="option.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="tw:flex tw:items-center tw:gap-2 tw:w-full tw:px-4 tw:py-2.5 tw:text-sm tw:font-medium tw:text-left tw:text-slate-200 tw:hover:bg-slate-700 tw:hover:text-white tw:transition-colors tw:duration-150 tw:group tw:no-underline"
                    >
                        <i
                            v-if="getIcon(option)"
                            :class="`fas fa-${getIcon(option)}`"
                            class="tw:w-4 tw:text-slate-400 tw:group-hover:text-white tw:transition-colors"
                            aria-hidden="true"
                        ></i>
                        {{ formatLabel(option.label) }}
                    </a>

                    <router-link
                        v-else-if="option.to"
                        :to="option.to"
                        class="tw:flex tw:items-center tw:gap-2 tw:w-full tw:px-4 tw:py-2.5 tw:text-sm tw:font-medium tw:text-left tw:text-slate-200 tw:hover:bg-slate-700 tw:hover:text-white tw:transition-colors tw:duration-150 tw:group tw:no-underline"
                    >
                        <i
                            v-if="getIcon(option)"
                            :class="`fas fa-${getIcon(option)}`"
                            class="tw:w-4 tw:text-slate-400 tw:group-hover:text-white tw:transition-colors"
                            aria-hidden="true"
                        ></i>
                        {{ formatLabel(option.label) }}
                    </router-link>
                </div>
            </div>
        </Portal>
    </div>
</template>

<script setup>
    import { watch, nextTick, ref, computed, onMounted, onBeforeUnmount } from 'vue'
    import { Portal } from 'portal-vue'
    import { useDropdownMenu } from '@/composables/admin/ui/useDropdownMenu.js'
    import Button from '@/modules/admin/ui/Button.vue'

    const props = defineProps({
        options:     { type: Array, required: true },
        customClass: { type: String, default: '' },
        dataCy:      { type: String, default: 'opciones' },
        label:       { type: String, default: 'OPCIONES' },
        disabled:    { type: Boolean, default: false },
        loading:     { type: Boolean, default: false },
        containerClass: { type: String, default: '' },
        forceOpenDown:   { type: Boolean, default: false },
    })

    const emit = defineEmits(['open', 'close'])

    /**
     * En móvil el botón ocupa el ancho completo de su contenedor (y de la
     * celda centrada de BaseTable) para que no quede pegado a la izquierda.
     * En `sm:` y superiores recupera el comportamiento original (ancho según
     * el consumidor vía `customClass`).
     */
    const buttonClass = computed(() =>
        ['tw:w-full tw:sm:w-auto', props.customClass].filter(Boolean).join(' ')
    )

    const {
        internalExpanded,
        dropdownRef,
        toggle,
        close,
        closeAndTrigger,
        getIcon,
        formatLabel,
        isDanger,
    } = useDropdownMenu(emit)

    const panelRef = ref(null)
    const resizeKey = ref(0)

    let rafId = null
    function onResizeOrScroll() {
        if (rafId) return
        rafId = requestAnimationFrame(() => {
            resizeKey.value++
            rafId = null
        })
    }

    const MARGIN = 8

    const panelStyle = ref({ display: 'none' })

    function calculateStyle() {
        if (!internalExpanded.value || !dropdownRef.value) {
            panelStyle.value = { display: 'none' }
            return
        }

        const trigger = dropdownRef.value.getBoundingClientRect()
        const windowWidth = window.innerWidth
        const windowHeight = window.innerHeight
        const isMobile = windowWidth < 640

        // Estimate panel height — use actual if available, fallback to estimate
        const panelEl = panelRef.value
        const estimatedHeight = panelEl?.offsetHeight || (props.options?.length || 4) * 44

        const spaceBelow = windowHeight - trigger.bottom - MARGIN
        const spaceAbove = trigger.top - MARGIN

        // Direccion del panel. Si forceOpenDown esta activo (primera fila del
        // listado de abonados), abrimos SIEMPRE hacia abajo para no cortar la
        // primera opcion. En el resto de filas se mantiene el comportamiento
        // original (elegir segun el espacio disponible y la altura estimada).
        const openDown = props.forceOpenDown
            ? true
            : (spaceBelow >= estimatedHeight || spaceBelow >= spaceAbove)

        if (isMobile) {
            const panelWidth = Math.min(220, windowWidth - 16)  // 16px margin
            const spaceLeft = trigger.left - panelWidth - 4

            if (openDown) {
                panelStyle.value = {
                    position: 'fixed',
                    top: trigger.top + 'px',
                    left: Math.max(4, spaceLeft) + 'px',
                    minWidth: panelWidth + 'px',
                    maxWidth: panelWidth + 'px',
                    maxHeight: spaceBelow + 'px',
                    zIndex: 50
                }
                return
            }
            // Open ABOVE
            const panelTop = trigger.bottom - estimatedHeight - MARGIN
            panelStyle.value = {
                position: 'fixed',
                top: Math.max(0, panelTop) + 'px',
                left: Math.max(4, spaceLeft) + 'px',
                minWidth: panelWidth + 'px',
                maxWidth: panelWidth + 'px',
                maxHeight: spaceAbove + 'px',
                zIndex: 50
            }
            return
        }

        // Desktop: panel opens to the LEFT of the trigger button
        const panelWidth = 220
        const spaceLeft = trigger.left - panelWidth - 4

        if (openDown) {
            const bottom = trigger.top + estimatedHeight + MARGIN
            const fitsVertically = bottom <= windowHeight
            panelStyle.value = {
                position: 'fixed',
                top: trigger.top + 'px',
                left: Math.max(4, spaceLeft) + 'px',
                minWidth: panelWidth + 'px',
                maxWidth: panelWidth + 'px',
                maxHeight: fitsVertically ? 'none' : (windowHeight - trigger.top - MARGIN) + 'px',
                zIndex: 50
            }
            return
        }
        const panelTop = trigger.bottom - estimatedHeight - MARGIN
        panelStyle.value = {
            position: 'fixed',
            top: Math.max(0, panelTop) + 'px',
            left: Math.max(4, spaceLeft) + 'px',
            minWidth: panelWidth + 'px',
            maxWidth: panelWidth + 'px',
            maxHeight: spaceAbove + 'px',
            zIndex: 50
        }
    }

    watch([internalExpanded, resizeKey], () => {
        nextTick(() => calculateStyle())
    })

    function handleClickOutside(event) {
        if (!internalExpanded.value) return
        if (dropdownRef.value?.contains(event.target)) return
        if (panelRef.value?.contains(event.target)) return
        close()
    }

    function addListeners() {
        document.addEventListener('click', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside, { passive: true })
    }

    function removeListeners() {
        document.removeEventListener('click', handleClickOutside)
        document.removeEventListener('touchstart', handleClickOutside)
    }

    watch(internalExpanded, expanded => {
        if (expanded) {
            addListeners()
        } else {
            removeListeners()
        }
    })

    onMounted(() => {
        window.addEventListener('resize', onResizeOrScroll)
        window.addEventListener('scroll', onResizeOrScroll, true)
    })

    onBeforeUnmount(() => {
        removeListeners()
        if (rafId) cancelAnimationFrame(rafId)
        window.removeEventListener('resize', onResizeOrScroll)
        window.removeEventListener('scroll', onResizeOrScroll, true)
    })

    defineExpose({
        close
    })
</script>
